import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { fetchPartnerUnseenCounts, markPartnerActivitySeen, type PartnerUnseenCounts, type PartnerActivityType } from '../lib/partner-notifications';

const EMPTY: PartnerUnseenCounts = { programs: 0 };

export function usePartnerNotifications() {
  const { user } = useAuth();
  const [counts, setCounts] = useState<PartnerUnseenCounts>(EMPTY);
  const [loading, setLoading] = useState(true);
  const refreshTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const refresh = useCallback(async () => {
    if (!user) return;
    try {
      const c = await fetchPartnerUnseenCounts(user.id);
      setCounts(c);
    } catch {
      setCounts(EMPTY);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    if (!user) return;

    const handleChange = () => {
      if (refreshTimer.current) clearTimeout(refreshTimer.current);
      refreshTimer.current = setTimeout(refresh, 500);
    };

    const channel = supabase
      .channel('partner-notifications')
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'program_assignments' }, handleChange)
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
      if (refreshTimer.current) clearTimeout(refreshTimer.current);
    };
  }, [user, refresh]);

  const markSeen = useCallback(async (activityType: PartnerActivityType) => {
    if (!user) return;
    setCounts(prev => ({ ...prev, [activityType]: 0 }));
    await markPartnerActivitySeen(user.id, activityType);
    refresh();
  }, [user, refresh]);

  return { counts, loading, markSeen, refresh };
}
